import BaseModel from './BaseModel';
import Pricing from './Pricing';
import Window from './Window';
import Ref from './Ref';
import * as _ from 'es-toolkit';
import { v4 } from 'uuid';

const clean_max_quantity = (target) => {
    if (!target)
        return;
    const max_quantity = Number(target.max_quantity);
    if (!Number.isFinite(max_quantity) || max_quantity < 1 || !Number.isInteger(max_quantity))
        delete target.max_quantity;
    else
        target.max_quantity = max_quantity;
};

const clean_tickettype_rules = (rules) => {
    if (!rules)
        return rules;

    const not_before = rules.not_before;
    if (!not_before || _.isNull(not_before) || not_before === "")
        delete rules.not_before;

    const not_after = rules.not_after;
    if (!not_after || _.isNull(not_after) || not_after === "")
        delete rules.not_after;

    if (_.isEmpty(rules.only_for_roles))
        delete rules.only_for_roles;

    if (_.isEmpty(rules.only_for_salepoints))
        delete rules.only_for_salepoints;

    if (_.isEmpty(rules.only_for_tickettypes))
        delete rules.only_for_tickettypes;

    if (_.isEmpty(rules.exclude_tickettypes))
        delete rules.exclude_tickettypes;

    if (_.isEmpty(rules.hides_pricings_while_available))
        delete rules.hides_pricings_while_available;

    if (!Array.isArray(rules.only_for_categories)) {
        delete rules.only_for_categories;
    } else {
        rules.only_for_categories = rules.only_for_categories.filter(
            c => typeof c === 'string' && c !== ''
        );
    }

    if (rules.stocks) {
        if (rules.stocks.all_pricings) {
            clean_max_quantity(rules.stocks.all_pricings);
            if (_.isEmpty(rules.stocks.all_pricings))
                delete rules.stocks.all_pricings;
        }
        Object.keys(rules.stocks.overrides || {}).forEach(id => {
            const override = rules.stocks.overrides[id];
            // Form delete can leave a {} tombstone; Engine rejects overrides
            // without a valid max_quantity (ENOPROP / EISNOTOBJ).
            if (!override || !_.isObject(override) || Array.isArray(override)) {
                delete rules.stocks.overrides[id];
                return;
            }
            clean_max_quantity(override);
            if (!override.independent_of_tickettype)
                delete override.independent_of_tickettype;
            if (!('max_quantity' in override))
                delete rules.stocks.overrides[id];
        });
        if (_.isEmpty(rules.stocks.overrides))
            delete rules.stocks.overrides;
        if (_.isEmpty(rules.stocks))
            delete rules.stocks;
    }

    return rules;
};

/** Class representing an tickettype. */
class Tickettype extends BaseModel {

    static ONE_TIME_PASS = 'one-time-pass';

    static RENEW_TYPE_NOW           = 'now';
    static RENEW_TYPE_EXP           = 'expiration';
    static RENEW_TYPE_START_OF_YEAR = 'start-of-year';
    static RENEW_TYPE_SOONEST       = 'soonest';
    static RENEW_TYPE_LATEST        = 'latest';

    static renewTypes = () => [
        Tickettype.RENEW_TYPE_NOW,
        Tickettype.RENEW_TYPE_EXP,
        Tickettype.RENEW_TYPE_START_OF_YEAR,
        Tickettype.RENEW_TYPE_SOONEST,
        Tickettype.RENEW_TYPE_LATEST
    ];

    constructor(properties) {
        super(properties);

        this.pricings = this.pricings || {};
        _.mapKeys(this.pricings, (pricing, key) => {
            this.pricings[key] = new Pricing(pricing);
        });
        this.refs = (this.refs || []).map(ref => new Ref(ref));

        if (!this.opaque.eshop_sort_weight)
            this.opaque.eshop_sort_weight = 0;
        Object.keys(this?.opaque?.layouts?.overrides||{}).forEach(o => {
            if (Array.isArray(this?.opaque?.layouts?.overrides[o]))
                this.opaque.layouts.overrides[o] = {}
        })

        this.windows = (this.windows || []).map(w => new Window(w));
    }

    /**
     * Prepare this object for update.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForUpdate() {
        const prepared = super.prepareForUpdate();
        _.mapKeys(this.pricings, (pricing, key) => {
            prepared.pricings[key] = pricing.prepareForUpdate();
        });

        if (!('_id' in prepared))
            prepared._id = v4();

        prepared.windows = (prepared.windows || []).map(window => {
            return window.prepareForUpdate();
        });

        if (prepared?.opaque?.layouts?.overrides) {
            Object.keys(prepared?.opaque?.layouts?.overrides).forEach(o => {
                Object.keys(prepared?.opaque?.layouts?.overrides[o]).forEach(l => {
                    if (prepared?.opaque?.layouts?.overrides[o][l] === "")
                        delete prepared?.opaque?.layouts?.overrides[o][l]
                })
            })
        }

        if (Array.isArray(prepared.posters)) {
            prepared.posters = prepared.posters.filter(p => _.isObject(p) && _.isString(p.url) && p.url.trim() !== "");
        } else {
            prepared.posters = [];
        }

        if (prepared.rules)
            clean_tickettype_rules(prepared.rules);
        // Keep an explicit rules object so Engine $set can clear previous defaults
        // when the UI removed every constraint key (including empty {}).
        if (prepared.rules && Object.keys(prepared.rules).length === 0)
            prepared.rules = {};

        // One-way purge of accidental legacy stocks (Engine owns migration).
        if (prepared?.opaque?.stocks)
            delete prepared.opaque.stocks;

        // OTP defaults live in settings.pdf_layouts.one_time_pass; never
        // re-persist opaque.layouts.{paper,thermal,card} (overrides stay).
        if (prepared?._id === Tickettype.ONE_TIME_PASS && prepared?.opaque?.layouts) {
            ['paper', 'thermal', 'card'].forEach((target) => {
                delete prepared.opaque.layouts[target];
            });
            if (
                prepared.opaque.layouts.overrides &&
                Object.keys(prepared.opaque.layouts.overrides).length === 0
            )
                delete prepared.opaque.layouts.overrides;
            if (Object.keys(prepared.opaque.layouts).length === 0)
                delete prepared.opaque.layouts;
        }

        return prepared;
    }

    /**
    * Prepare this object for duplicate.
    * This is used to "normalize", if needed, some properties
    * before to send them.
    *
    * @param {Object} optional default properties
    * 
    * return{BaseModel}
    */
    prepareForDuplicate(options) {
        const prepared = _.cloneDeep(this);
        prepared._id = options._id;
        prepared.name = options.name;
        if ('description' in (options || {}))
            prepared.description = options.description;
        prepared.refs = [];

        return prepared;
    }
}

export default Tickettype;
